import { useState, useEffect } from 'react';
import { 
  Book, Code, Zap, Shield, Key, AlertTriangle, CheckCircle, 
  Copy, ChevronRight, ExternalLink, Terminal, FileJson,
  Cpu, Layers, Image, Link as LinkIcon, Play, Clock, 
  ArrowRight, Menu, X, Search, Hash
} from 'lucide-react';
import { Highlight, themes } from 'prism-react-renderer';
import toast from 'react-hot-toast';

const DocsPage = () => {
  const [activeSection, setActiveSection] = useState('introduction');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const sections = [
    { id: 'introduction', title: 'Introduction', icon: Book },
    { id: 'quickstart', title: 'Quick Start', icon: Zap },
    { id: 'authentication', title: 'Authentication', icon: Key },
    { id: 'endpoints', title: 'API Endpoints', icon: Code },
    { id: 'single', title: 'Single Image Analysis', icon: Image, indent: true },
    { id: 'multi', title: 'Batch Analysis', icon: Layers, indent: true },
    { id: 'urls', title: 'URL Analysis', icon: LinkIcon, indent: true },
    { id: 'streaming', title: 'Streaming Analysis', icon: Zap, indent: true },
    { id: 'responses', title: 'Response Format', icon: FileJson },
    { id: 'errors', title: 'Error Handling', icon: AlertTriangle },
    { id: 'rate-limits', title: 'Rate Limits', icon: Clock },
    { id: 'models', title: 'AI Models', icon: Cpu },
    { id: 'examples', title: 'Code Examples', icon: Terminal },
  ];

  // Scroll spy effect
  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => document.getElementById(s.id));
      const scrollPosition = window.scrollY + 150;

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const section = sectionElements[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
    setSidebarOpen(false);
  };

  const CodeBlock = ({ code, language = 'javascript', title }) => {
    const copyCode = () => {
      navigator.clipboard.writeText(code);
      toast.success('Code copied!');
    };

    return (
      <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-dark-700 my-4">
        {title && (
          <div className="bg-gray-100 dark:bg-dark-800 px-3 md:px-4 py-2 flex items-center justify-between border-b border-gray-200 dark:border-dark-700">
            <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">{title}</span>
            <button
              onClick={copyCode}
              className="p-1.5 ml-2 hover:bg-gray-200 dark:hover:bg-dark-700 rounded transition-colors flex-shrink-0"
              title="Copy code"
            >
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        )}
        <Highlight theme={themes.nightOwl} code={code.trim()} language={language}>
          {({ className, style, tokens, getLineProps, getTokenProps }) => (
            <pre className={`${className} p-2 md:p-4 overflow-x-auto text-xs md:text-sm`} style={style}>
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line })}>
                  <span className="text-gray-500 mr-2 md:mr-4 select-none w-4 md:w-6 inline-block text-right">
                    {i + 1}
                  </span>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              ))}
            </pre>
          )}
        </Highlight>
      </div>
    );
  };

  const EndpointCard = ({ method, path, description, badge }) => (
    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 p-3 md:p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 hover:border-primary-500 transition-colors">
      <span className={`px-2 py-1 rounded text-xs font-bold w-fit ${
        method === 'POST' 
          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
      }`}>
        {method}
      </span>
      <code className="text-xs md:text-sm font-mono text-gray-800 dark:text-gray-200 flex-1 break-all md:break-normal">{path}</code>
      <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">{description}</span>
      {badge && (
        <span className="px-2 py-0.5 bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 rounded text-xs flex-shrink-0">
          {badge}
        </span>
      )}
    </div>
  );

  const InfoBox = ({ type = 'info', title, children }) => {
    const styles = {
      info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200',
      warning: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200',
      success: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200',
      error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
    };

    const icons = {
      info: <Book className="w-4 md:w-5 h-4 md:h-5" />,
      warning: <AlertTriangle className="w-4 md:w-5 h-4 md:h-5" />,
      success: <CheckCircle className="w-4 md:w-5 h-4 md:h-5" />,
      error: <AlertTriangle className="w-4 md:w-5 h-4 md:h-5" />,
    };

    return (
      <div className={`p-3 md:p-4 rounded-lg border ${styles[type]} my-4`}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">{icons[type]}</div>
          <div className="min-w-0">
            {title && <p className="font-semibold mb-1 text-sm md:text-base">{title}</p>}
            <div className="text-xs md:text-sm opacity-90">{children}</div>
          </div>
        </div>
      </div>
    );
  };

  const TabGroup = ({ tabs }) => {
    const [active, setActive] = useState(0);
    return (
      <div className="my-4">
        <div className="flex border-b border-gray-200 dark:border-dark-700 overflow-x-auto">
          {tabs.map((tab, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                active === i
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="mt-4">
          {tabs[active].content}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white dark:bg-dark-950 pt-16">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed bottom-4 right-4 z-40 lg:hidden p-4 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside className={`fixed top-16 left-0 h-[calc(100vh-64px)] w-64 md:w-72 bg-gray-50 dark:bg-dark-900 border-r border-gray-200 dark:border-dark-800 z-40 transform transition-transform duration-300 lg:translate-x-0 overflow-y-auto ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-4 md:p-6 border-b border-gray-200 dark:border-dark-800">
          <a href="/" className="flex items-center gap-2">
            <Shield className="w-6 md:w-8 h-6 md:h-8 text-primary-600" />
            <span className="text-lg md:text-xl font-bold">NSFW API</span>
          </a>
          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">Documentation</p>
        </div>

        {/* Search */}
        <div className="p-3 md:p-4 border-b border-gray-200 dark:border-dark-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search docs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-3 md:p-4 overflow-y-auto h-[calc(100vh-220px)] md:h-[calc(100vh-240px)]">
          <ul className="space-y-1">
            {sections
              .filter(s => s.title.toLowerCase().includes(searchQuery.toLowerCase()))
              .map((section) => (
                <li key={section.id}>
                  <button
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                      section.indent ? 'ml-4' : ''
                    } ${
                      activeSection === section.id
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800'
                    }`}
                  >
                    <section.icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{section.title}</span>
                  </button>
                </li>
              ))}
          </ul>
        </nav>
      </aside>

      {/* Main content */}
      <main className="lg:ml-72 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          
          {/* Introduction */}
          <section id="introduction" className="scroll-mt-24">
            <div className="flex items-center gap-2 text-primary-600 text-xs md:text-sm font-medium mb-2">
              <Book className="w-4 h-4" />
              Documentation
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">NSFW Detection API</h1>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-8">
              A powerful API for detecting NSFW content in images using state-of-the-art 
              Vision Transformer models with ensemble learning.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <Zap className="w-6 md:w-8 h-6 md:h-8 text-blue-600 mb-2" />
                <h3 className="font-semibold text-sm md:text-base mb-1">Fast & Accurate</h3>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Triple-model ensemble for maximum accuracy</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <Shield className="w-6 md:w-8 h-6 md:h-8 text-green-600 mb-2" />
                <h3 className="font-semibold text-sm md:text-base mb-1">Secure</h3>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">API key authentication & rate limiting</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <Layers className="w-6 md:w-8 h-6 md:h-8 text-purple-600 mb-2" />
                <h3 className="font-semibold text-sm md:text-base mb-1">Scalable</h3>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Batch processing & streaming support</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row flex-wrap gap-4">
              <a href="/dashboard/keys" className="btn-primary inline-flex items-center justify-center md:justify-start gap-2 w-full md:w-auto">
                Get API Key <ArrowRight className="w-4 h-4" />
              </a>
              <a href="/dashboard/playground" className="btn-secondary inline-flex items-center justify-center md:justify-start gap-2 w-full md:w-auto">
                <Play className="w-4 h-4" /> Try Playground
              </a>
            </div>
          </section>

          <hr className="my-12 border-gray-200 dark:border-dark-800" />

          {/* Quick Start */}
          <section id="quickstart" className="scroll-mt-24">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 flex items-center gap-2">
              <Hash className="w-5 md:w-6 h-5 md:h-6 text-primary-600" />
              Quick Start
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm md:text-base">
              Get started with the NSFW Detection API in just a few minutes.
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm md:text-base">1</div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold mb-2 text-sm md:text-base">Get your API Key</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs md:text-sm mb-2">
                    Sign up and create an API key from your dashboard.
                  </p>
                  <a href="/dashboard/keys" className="text-primary-600 text-xs md:text-sm hover:underline inline-flex items-center gap-1">
                    Go to API Keys <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm md:text-base">2</div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold mb-2 text-sm md:text-base">Make your first request</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs md:text-sm mb-2">
                    Use the API key to analyze an image.
                  </p>
                  <CodeBlock
                    language="bash"
                    title="cURL"
                    code={`curl -X POST "https://nsfwbackend.techycsr.dev/api/analyze/single" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -F "url=https://example.com/image.jpg"`}
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm md:text-base">3</div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold mb-2 text-sm md:text-base">Get the result</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs md:text-sm mb-2">
                    The API returns a JSON response with the analysis result.
                  </p>
                  <CodeBlock
                    language="json"
                    title="Response"
                    code={`{
  "success": true,
  "data": {
    "final_decision": "Safe",
    "confidence": 0.92,
    "ensemble_category": "male_only",
    "models_used": ["ViT-L-14", "ViT-H-14", "ViT-g-14"]
  }
}`}
                  />
                </div>
              </div>
            </div>
          </section>

          <hr className="my-12 border-gray-200 dark:border-dark-800" />

          {/* Authentication */}
          <section id="authentication" className="scroll-mt-24">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 flex items-center gap-2">
              <Hash className="w-5 md:w-6 h-5 md:h-6 text-primary-600" />
              Authentication
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm md:text-base">
              All API requests require authentication using an API key.
            </p>

            <InfoBox type="info" title="API Key Header">
              Include your API key in the <code className="bg-gray-200 dark:bg-dark-700 px-1 rounded text-xs md:text-sm">X-API-Key</code> header with every request.
            </InfoBox>

            <CodeBlock
              language="bash"
              title="Example Request"
              code={`curl -X POST "https://nsfwbackend.techycsr.dev/api/analyze/single" \\
  -H "X-API-Key: nsfw_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" \\
  -F "file=@image.jpg"`}
            />

            <InfoBox type="warning" title="Keep your API key secure">
              Never expose your API key in client-side code or public repositories. 
              Use environment variables or secure key management solutions.
            </InfoBox>

            <h3 className="text-lg md:text-xl font-semibold mt-8 mb-4">Managing API Keys</h3>
            <ul className="space-y-2 text-gray-600 dark:text-gray-400 text-sm md:text-base">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Create up to 3 API keys per account (free tier)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Revoke and regenerate keys at any time</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Track usage per API key in the dashboard</span>
              </li>
            </ul>
          </section>

          <hr className="my-12 border-gray-200 dark:border-dark-800" />

          {/* API Endpoints */}
          <section id="endpoints" className="scroll-mt-24">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 flex items-center gap-2">
              <Hash className="w-5 md:w-6 h-5 md:h-6 text-primary-600" />
              API Endpoints
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Base URL: <code className="bg-gray-200 dark:bg-dark-700 px-2 py-1 rounded">https://nsfwbackend.techycsr.dev</code>
            </p>

            <div className="space-y-3">
              <EndpointCard method="POST" path="/api/analyze/single" description="Single image analysis" />
              <EndpointCard method="POST" path="/api/analyze/multi" description="Batch analysis (up to 10)" />
              <EndpointCard method="POST" path="/api/analyze/urls" description="URL-based analysis" />
              <EndpointCard method="POST" path="/api/analyze/streaming" description="Real-time streaming" badge="SSE" />
              <EndpointCard method="GET" path="/api/analyze/health" description="API health check" />
              <EndpointCard method="GET" path="/api/analyze/config" description="API configuration" />
            </div>
          </section>

          <hr className="my-12 border-gray-200 dark:border-dark-800" />

          {/* Single Image Analysis */}
          <section id="single" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Hash className="w-6 h-6 text-primary-600" />
              Single Image Analysis
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Analyze a single image for NSFW content using file upload or URL.
            </p>

            <div className="bg-gray-50 dark:bg-dark-800 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded text-xs font-bold">POST</span>
                <code className="text-sm">/api/analyze/single</code>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Content-Type: <code className="bg-gray-200 dark:bg-dark-700 px-1 rounded">multipart/form-data</code>
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-3">Request Parameters</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 dark:border-dark-700 rounded-lg overflow-hidden">
                <thead className="bg-gray-50 dark:bg-dark-800">
                  <tr>
                    <th className="text-left p-3 font-semibold">Parameter</th>
                    <th className="text-left p-3 font-semibold">Type</th>
                    <th className="text-left p-3 font-semibold">Required</th>
                    <th className="text-left p-3 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                  <tr>
                    <td className="p-3"><code>file</code></td>
                    <td className="p-3">File</td>
                    <td className="p-3"><span className="text-yellow-600">*</span></td>
                    <td className="p-3">Image file (JPEG, PNG, WebP, GIF, AVIF, BMP, TIFF)</td>
                  </tr>
                  <tr>
                    <td className="p-3"><code>url</code></td>
                    <td className="p-3">String</td>
                    <td className="p-3"><span className="text-yellow-600">*</span></td>
                    <td className="p-3">Image URL (alternative to file upload)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-500 mt-2">* Either <code>file</code> or <code>url</code> is required</p>

            <TabGroup tabs={[
              {
                label: 'cURL',
                content: (
                  <CodeBlock
                    language="bash"
                    code={`# Using file upload
curl -X POST "https://nsfwbackend.techycsr.dev/api/analyze/single" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -F "file=@image.jpg"

# Using URL
curl -X POST "https://nsfwbackend.techycsr.dev/api/analyze/single" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -F "url=https://example.com/image.jpg"`}
                  />
                )
              },
              {
                label: 'JavaScript',
                content: (
                  <CodeBlock
                    language="javascript"
                    code={`// Using file upload
const formData = new FormData();
formData.append('file', fileInput.files[0]);

const response = await fetch('https://nsfwbackend.techycsr.dev/api/analyze/single', {
  method: 'POST',
  headers: { 'X-API-Key': 'YOUR_API_KEY' },
  body: formData
});

const result = await response.json();
console.log(result);`}
                  />
                )
              },
              {
                label: 'Python',
                content: (
                  <CodeBlock
                    language="python"
                    code={`import requests

# Using file upload
with open('image.jpg', 'rb') as f:
    response = requests.post(
        'https://nsfwbackend.techycsr.dev/api/analyze/single',
        headers={'X-API-Key': 'YOUR_API_KEY'},
        files={'file': f}
    )

print(response.json())`}
                  />
                )
              },
              {
                label: 'Node.js',
                content: (
                  <CodeBlock
                    language="javascript"
                    code={`const axios = require('axios');
const FormData = require('form-data');
const fs = require('fs');

const formData = new FormData();
formData.append('file', fs.createReadStream('image.jpg'));

axios.post('https://nsfwbackend.techycsr.dev/api/analyze/single', formData, {
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    ...formData.getHeaders()
  }
})
.then(res => console.log(res.data))
.catch(err => console.error(err.response.data));`}
                  />
                )
              }
            ]} />
          </section>

          <hr className="my-12 border-gray-200 dark:border-dark-800" />

          {/* Batch Analysis */}
          <section id="multi" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Hash className="w-6 h-6 text-primary-600" />
              Batch Analysis
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Analyze multiple images in a single request (up to 10 images).
            </p>

            <div className="bg-gray-50 dark:bg-dark-800 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded text-xs font-bold">POST</span>
                <code className="text-sm">/api/analyze/multi</code>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Content-Type: <code className="bg-gray-200 dark:bg-dark-700 px-1 rounded">multipart/form-data</code>
              </p>
            </div>

            <InfoBox type="info">
              Use the field name <code className="bg-gray-200 dark:bg-dark-600 px-1 rounded">files</code> for each image. 
              Maximum 10 files per request, 500MB total size limit.
            </InfoBox>

            <CodeBlock
              language="bash"
              title="cURL Example"
              code={`curl -X POST "https://nsfwbackend.techycsr.dev/api/analyze/multi" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -F "files=@image1.jpg" \\
  -F "files=@image2.jpg" \\
  -F "files=@image3.jpg"`}
            />

            <CodeBlock
              language="json"
              title="Response"
              code={`{
  "success": true,
  "data": {
    "predictions": [
      {
        "image": "image1.jpg",
        "final_decision": "Safe",
        "confidence": 0.92,
        "ensemble_category": "male_only"
      },
      {
        "image": "image2.jpg",
        "final_decision": "NSFW",
        "confidence": 0.87,
        "ensemble_category": "female_present"
      }
    ],
    "summary": {
      "total_images": 2,
      "safe_images": 1,
      "nsfw_images": 1,
      "total_time_ms": 2450.5
    }
  }
}`}
            />
          </section>

          <hr className="my-12 border-gray-200 dark:border-dark-800" />

          {/* URL Analysis */}
          <section id="urls" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Hash className="w-6 h-6 text-primary-600" />
              URL Analysis
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Analyze images directly from URLs without uploading files.
            </p>

            <div className="bg-gray-50 dark:bg-dark-800 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded text-xs font-bold">POST</span>
                <code className="text-sm">/api/analyze/urls</code>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Content-Type: <code className="bg-gray-200 dark:bg-dark-700 px-1 rounded">application/json</code>
              </p>
            </div>

            <CodeBlock
              language="bash"
              title="cURL Example"
              code={`curl -X POST "https://nsfwbackend.techycsr.dev/api/analyze/urls" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "urls": [
      "https://example.com/image1.jpg",
      "https://example.com/image2.png"
    ]
  }'`}
            />

            <CodeBlock
              language="javascript"
              title="JavaScript Example"
              code={`const response = await fetch('https://nsfwbackend.techycsr.dev/api/analyze/urls', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    urls: [
      'https://example.com/image1.jpg',
      'https://example.com/image2.png'
    ]
  })
});

const result = await response.json();
console.log(result);`}
            />
          </section>

          <hr className="my-12 border-gray-200 dark:border-dark-800" />

          {/* Streaming Analysis */}
          <section id="streaming" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Hash className="w-6 h-6 text-primary-600" />
              Streaming Analysis
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Process large batches of images with real-time streaming results using Server-Sent Events (SSE).
            </p>

            <div className="bg-gray-50 dark:bg-dark-800 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded text-xs font-bold">POST</span>
                <code className="text-sm">/api/analyze/streaming</code>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Response: <code className="bg-gray-200 dark:bg-dark-700 px-1 rounded">text/event-stream</code> (SSE)
              </p>
            </div>

            <InfoBox type="success" title="High Volume Processing">
              Streaming endpoint supports up to 100 images per request and returns results as they are processed.
            </InfoBox>

            <CodeBlock
              language="javascript"
              title="JavaScript Streaming Example"
              code={`const formData = new FormData();
files.forEach(file => formData.append('files', file));

const response = await fetch('https://nsfwbackend.techycsr.dev/api/analyze/streaming', {
  method: 'POST',
  headers: { 'X-API-Key': 'YOUR_API_KEY' },
  body: formData
});

const reader = response.body.getReader();
const decoder = new TextDecoder();

while (true) {
  const { done, value } = await reader.read();
  if (done) break;
  
  const text = decoder.decode(value);
  const lines = text.split('\\n');
  
  lines.forEach(line => {
    if (line.startsWith('data: ')) {
      const data = JSON.parse(line.substring(6));
      console.log('Result:', data);
      
      // Handle progress
      if (data.progress) {
        console.log(\`Progress: \${data.progress.current}/\${data.progress.total}\`);
      }
      
      // Handle summary
      if (data.type === 'summary') {
        console.log('Complete!', data);
      }
    }
  });
}`}
            />
          </section>

          <hr className="my-12 border-gray-200 dark:border-dark-800" />

          {/* Response Format */}
          <section id="responses" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Hash className="w-6 h-6 text-primary-600" />
              Response Format
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              All API responses follow a consistent JSON structure.
            </p>

            <h3 className="text-lg font-semibold mb-3">Success Response</h3>
            <CodeBlock
              language="json"
              code={`{
  "success": true,
  "data": {
    "image": "example.jpg",
    "final_decision": "NSFW",
    "confidence": 0.87,
    "ensemble_category": "female_present",
    "total_time_ms": 1250.5,
    "models_used": ["ViT-L-14", "ViT-H-14", "ViT-g-14"],
    "results": {
      "triple_ensemble": {
        "ensemble_prediction": {
          "predicted_category": "female_present",
          "confidence": 0.87,
          "safety_decision": "UNSAFE"
        }
      }
    }
  },
  "metadata": {
    "requestId": "abc123",
    "processingTime": 1250
  }
}`}
            />

            <h3 className="text-lg font-semibold mt-8 mb-3">Classification Categories</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 dark:border-dark-700 rounded-lg overflow-hidden">
                <thead className="bg-gray-50 dark:bg-dark-800">
                  <tr>
                    <th className="text-left p-3 font-semibold">Category</th>
                    <th className="text-left p-3 font-semibold">Decision</th>
                    <th className="text-left p-3 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                  <tr>
                    <td className="p-3"><code>male_only</code></td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-bold">SAFE</span></td>
                    <td className="p-3">Single men without women</td>
                  </tr>
                  <tr>
                    <td className="p-3"><code>female_present</code></td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-bold">NSFW</span></td>
                    <td className="p-3">Any woman/girl detected</td>
                  </tr>
                  <tr>
                    <td className="p-3"><code>adult_object</code></td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-bold">NSFW</span></td>
                    <td className="p-3">Explicit content or adult toys</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <hr className="my-12 border-gray-200 dark:border-dark-800" />

          {/* Error Handling */}
          <section id="errors" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Hash className="w-6 h-6 text-primary-600" />
              Error Handling
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              The API uses standard HTTP status codes and returns detailed error messages.
            </p>

            <h3 className="text-lg font-semibold mb-3">Error Response Format</h3>
            <CodeBlock
              language="json"
              code={`{
  "success": false,
  "error": "Invalid API key",
  "statusCode": 401,
  "requestId": "abc123"
}`}
            />

            <h3 className="text-lg font-semibold mt-8 mb-3">HTTP Status Codes</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold">200</span>
                <span className="font-medium">OK</span>
                <span className="text-gray-600 dark:text-gray-400 text-sm">Request successful</span>
              </div>
              <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-bold">400</span>
                <span className="font-medium">Bad Request</span>
                <span className="text-gray-600 dark:text-gray-400 text-sm">Invalid request parameters</span>
              </div>
              <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
                <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-bold">401</span>
                <span className="font-medium">Unauthorized</span>
                <span className="text-gray-600 dark:text-gray-400 text-sm">Missing or invalid API key</span>
              </div>
              <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-bold">429</span>
                <span className="font-medium">Too Many Requests</span>
                <span className="text-gray-600 dark:text-gray-400 text-sm">Rate limit exceeded</span>
              </div>
              <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
                <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-bold">500</span>
                <span className="font-medium">Server Error</span>
                <span className="text-gray-600 dark:text-gray-400 text-sm">Internal server error</span>
              </div>
            </div>
          </section>

          <hr className="my-12 border-gray-200 dark:border-dark-800" />

          {/* Rate Limits */}
          <section id="rate-limits" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Hash className="w-6 h-6 text-primary-600" />
              Rate Limits
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              API requests are rate limited to ensure fair usage and system stability.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 dark:border-dark-700 rounded-lg overflow-hidden">
                <thead className="bg-gray-50 dark:bg-dark-800">
                  <tr>
                    <th className="text-left p-3 font-semibold">Plan</th>
                    <th className="text-left p-3 font-semibold">Requests/Minute</th>
                    <th className="text-left p-3 font-semibold">Requests/Day</th>
                    <th className="text-left p-3 font-semibold">Max API Keys</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                  <tr>
                    <td className="p-3 font-medium">Free</td>
                    <td className="p-3">60</td>
                    <td className="p-3">1,000</td>
                    <td className="p-3">3</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-medium">Pro</td>
                    <td className="p-3">300</td>
                    <td className="p-3">50,000</td>
                    <td className="p-3">10</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-medium">Enterprise</td>
                    <td className="p-3">Custom</td>
                    <td className="p-3">Unlimited</td>
                    <td className="p-3">Unlimited</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <InfoBox type="warning" title="Rate Limit Exceeded">
              When you exceed the rate limit, the API returns a <code>429 Too Many Requests</code> response. 
              Wait for the rate limit window to reset before making more requests.
            </InfoBox>
          </section>

          <hr className="my-12 border-gray-200 dark:border-dark-800" />

          {/* AI Models */}
          <section id="models" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Hash className="w-6 h-6 text-primary-600" />
              AI Models
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              The API uses a Triple-Model Ensemble System combining three state-of-the-art Vision Transformers.
            </p>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800 text-center">
                <div className="text-3xl font-bold text-blue-600 mb-1">40%</div>
                <div className="font-semibold">ViT-L-14</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Large Vision Transformer</div>
                <div className="text-xs text-gray-500 mt-1">Primary Model</div>
              </div>
              <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-purple-200 dark:border-purple-800 text-center">
                <div className="text-3xl font-bold text-purple-600 mb-1">35%</div>
                <div className="font-semibold">ViT-H-14</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Huge Vision Transformer</div>
                <div className="text-xs text-gray-500 mt-1">Enhanced Accuracy</div>
              </div>
              <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800 text-center">
                <div className="text-3xl font-bold text-green-600 mb-1">25%</div>
                <div className="font-semibold">ViT-g-14</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Giant Vision Transformer</div>
                <div className="text-xs text-gray-500 mt-1">Maximum Capacity</div>
              </div>
            </div>

            <h3 className="text-lg font-semibold mb-3">Ensemble Method</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              The ensemble uses <strong>weighted averaging</strong> to combine predictions from all three models.
              This approach provides:
            </p>
            <ul className="space-y-2 text-gray-600 dark:text-gray-400">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Higher accuracy through model agreement</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Reduced false positives/negatives</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Confidence scores reflecting model consensus</span>
              </li>
            </ul>
          </section>

          <hr className="my-12 border-gray-200 dark:border-dark-800" />

          {/* Code Examples */}
          <section id="examples" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Hash className="w-6 h-6 text-primary-600" />
              Code Examples
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Complete examples in various programming languages.
            </p>

            <TabGroup tabs={[
              {
                label: 'Python',
                content: (
                  <CodeBlock
                    language="python"
                    code={`import requests

API_KEY = "your_api_key_here"
BASE_URL = "https://nsfwbackend.techycsr.dev"

def analyze_image(image_path):
    """Analyze a single image for NSFW content."""
    with open(image_path, 'rb') as f:
        response = requests.post(
            f"{BASE_URL}/api/analyze/single",
            headers={"X-API-Key": API_KEY},
            files={"file": f}
        )
    
    result = response.json()
    
    if result["success"]:
        data = result["data"]
        print(f"Decision: {data['final_decision']}")
        print(f"Confidence: {data['confidence']:.2%}")
        print(f"Category: {data['ensemble_category']}")
    else:
        print(f"Error: {result['error']}")
    
    return result

def analyze_batch(image_paths):
    """Analyze multiple images in batch."""
    files = [("files", open(path, "rb")) for path in image_paths]
    
    response = requests.post(
        f"{BASE_URL}/api/analyze/multi",
        headers={"X-API-Key": API_KEY},
        files=files
    )
    
    # Close file handles
    for _, f in files:
        f.close()
    
    return response.json()

# Example usage
if __name__ == "__main__":
    result = analyze_image("test_image.jpg")
    print(result)`}
                  />
                )
              },
              {
                label: 'JavaScript',
                content: (
                  <CodeBlock
                    language="javascript"
                    code={`const API_KEY = 'your_api_key_here';
const BASE_URL = 'https://nsfwbackend.techycsr.dev';

// Analyze single image from file input
async function analyzeImage(file) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(\`\${BASE_URL}/api/analyze/single\`, {
    method: 'POST',
    headers: { 'X-API-Key': API_KEY },
    body: formData
  });

  const result = await response.json();
  
  if (result.success) {
    console.log('Decision:', result.data.final_decision);
    console.log('Confidence:', (result.data.confidence * 100).toFixed(1) + '%');
    console.log('Category:', result.data.ensemble_category);
  } else {
    console.error('Error:', result.error);
  }
  
  return result;
}

// Analyze images from URLs
async function analyzeUrls(urls) {
  const response = await fetch(\`\${BASE_URL}/api/analyze/urls\`, {
    method: 'POST',
    headers: {
      'X-API-Key': API_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ urls })
  });

  return response.json();
}

// Example: Handle file input
document.getElementById('fileInput').addEventListener('change', async (e) => {
  const file = e.target.files[0];
  if (file) {
    const result = await analyzeImage(file);
    console.log(result);
  }
});`}
                  />
                )
              },
              {
                label: 'PHP',
                content: (
                  <CodeBlock
                    language="php"
                    code={`<?php

$apiKey = 'your_api_key_here';
$baseUrl = 'https://nsfwbackend.techycsr.dev';

function analyzeImage($imagePath) {
    global $apiKey, $baseUrl;
    
    $curl = curl_init();
    
    curl_setopt_array($curl, [
        CURLOPT_URL => $baseUrl . '/api/analyze/single',
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_POST => true,
        CURLOPT_HTTPHEADER => [
            'X-API-Key: ' . $apiKey
        ],
        CURLOPT_POSTFIELDS => [
            'file' => new CURLFile($imagePath)
        ]
    ]);
    
    $response = curl_exec($curl);
    curl_close($curl);
    
    return json_decode($response, true);
}

function analyzeUrls($urls) {
    global $apiKey, $baseUrl;
    
    $curl = curl_init();
    
    curl_setopt_array($curl, [
        CURLOPT_URL => $baseUrl . '/api/analyze/urls',
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_POST => true,
        CURLOPT_HTTPHEADER => [
            'X-API-Key: ' . $apiKey,
            'Content-Type: application/json'
        ],
        CURLOPT_POSTFIELDS => json_encode(['urls' => $urls])
    ]);
    
    $response = curl_exec($curl);
    curl_close($curl);
    
    return json_decode($response, true);
}

// Example usage
$result = analyzeImage('test_image.jpg');
print_r($result);
?>`}
                  />
                )
              },
              {
                label: 'Go',
                content: (
                  <CodeBlock
                    language="go"
                    code={`package main

import (
    "bytes"
    "encoding/json"
    "fmt"
    "io"
    "mime/multipart"
    "net/http"
    "os"
)

const (
    apiKey  = "your_api_key_here"
    baseURL = "https://nsfwbackend.techycsr.dev"
)

type AnalysisResult struct {
    Success bool \`json:"success"\`
    Data    struct {
        FinalDecision    string   \`json:"final_decision"\`
        Confidence       float64  \`json:"confidence"\`
        EnsembleCategory string   \`json:"ensemble_category"\`
        ModelsUsed       []string \`json:"models_used"\`
    } \`json:"data"\`
    Error string \`json:"error,omitempty"\`
}

func analyzeImage(imagePath string) (*AnalysisResult, error) {
    file, err := os.Open(imagePath)
    if err != nil {
        return nil, err
    }
    defer file.Close()

    body := &bytes.Buffer{}
    writer := multipart.NewWriter(body)
    
    part, err := writer.CreateFormFile("file", imagePath)
    if err != nil {
        return nil, err
    }
    
    _, err = io.Copy(part, file)
    if err != nil {
        return nil, err
    }
    writer.Close()

    req, err := http.NewRequest("POST", baseURL+"/api/analyze/single", body)
    if err != nil {
        return nil, err
    }
    
    req.Header.Set("X-API-Key", apiKey)
    req.Header.Set("Content-Type", writer.FormDataContentType())

    client := &http.Client{}
    resp, err := client.Do(req)
    if err != nil {
        return nil, err
    }
    defer resp.Body.Close()

    var result AnalysisResult
    if err := json.NewDecoder(resp.Body).Decode(&result); err != nil {
        return nil, err
    }

    return &result, nil
}

func main() {
    result, err := analyzeImage("test_image.jpg")
    if err != nil {
        fmt.Println("Error:", err)
        return
    }
    
    if result.Success {
        fmt.Printf("Decision: %s\\n", result.Data.FinalDecision)
        fmt.Printf("Confidence: %.1f%%\\n", result.Data.Confidence*100)
    } else {
        fmt.Println("Error:", result.Error)
    }
}`}
                  />
                )
              }
            ]} />
          </section>

          {/* Footer */}
          <div className="mt-16 p-6 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="font-semibold mb-1">Need help?</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Try the API in our interactive playground or contact support.
                </p>
              </div>
              <div className="flex gap-3">
                <a href="/dashboard/playground" className="btn-primary inline-flex items-center gap-2">
                  <Play className="w-4 h-4" /> Try Playground
                </a>
                <a href="mailto:support@techycsr.dev" className="btn-secondary inline-flex items-center gap-2">
                  Contact Support
                </a>
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DocsPage;
